import {
  TENDER_NOTICE_TYPE_LABELS,
  daysUntil,
  formatDate,
  type TenderDto,
} from '@nova/shared';
import { CalendarClock, Radar, Search } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EmptyState } from '../components/EmptyState.js';
import { GlassPanel } from '../components/GlassPanel.js';
import { SkeletonPanel } from '../components/Skeleton.js';
import { cn } from '../lib/cn.js';
import { TenderDetail } from '../features/sales/TenderDetail.js';
import { TenderStatusBadge } from '../features/sales/TenderStatusBadge.js';
import { WatchProfiles } from '../features/sales/WatchProfiles.js';
import { useCrawlRuns, useTenders, useWatchProfiles, type TenderFilters } from '../features/sales/useSales.js';

type Tab = 'anbud' | 'profiler';

export function SalgPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [tab, setTab] = useState<Tab>('anbud');

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-ink flex items-center gap-2 text-xl">
            <Radar size={20} aria-hidden /> Anbud
          </h1>
          <p className="text-ink-soft mt-0.5 text-sm">
            Anbudsradaren — daglige Doffin-treff, sortert etter relevans.
          </p>
        </div>
        <div className="border-hairline flex gap-1 rounded-lg border p-0.5">
          <TabButton active={tab === 'anbud'} onClick={() => setTab('anbud')}>
            Anbud
          </TabButton>
          <TabButton active={tab === 'profiler'} onClick={() => setTab('profiler')}>
            Overvåkningsprofiler
          </TabButton>
        </div>
      </header>

      {tab === 'anbud' ? <TenderListView /> : <WatchProfiles />}

      <TenderDetail id={id ?? null} onClose={() => navigate('/salg')} />
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded-md px-3 py-1.5 text-sm transition-colors',
        active ? 'bg-accent/15 text-accent' : 'text-ink-soft hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

function TenderListView() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<TenderFilters>({ sort: 'relevance' });
  const { data, isLoading } = useTenders(filters);
  const { data: profiles = [] } = useWatchProfiles();
  const { data: runs = [] } = useCrawlRuns(1);

  const areas = [...new Set(profiles.map((p) => p.productArea))];
  const lastRun = runs[0];

  return (
    <div className="space-y-4">
      {/* Filterlinje */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={14} className="text-ink-soft absolute left-2.5 top-1/2 -translate-y-1/2" aria-hidden />
          <input
            className="border-hairline bg-base/40 text-ink w-52 rounded-lg border py-1.5 pl-8 pr-3 text-sm"
            placeholder="Søk i tittel/oppdragsgiver"
            value={filters.search ?? ''}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value || undefined }))}
          />
        </div>
        <Select
          value={filters.sort ?? 'relevance'}
          onChange={(v) => setFilters((f) => ({ ...f, sort: v as TenderFilters['sort'] }))}
          options={[
            ['relevance', 'Sorter: relevans'],
            ['deadline', 'Sorter: frist'],
            ['published', 'Sorter: nyeste'],
          ]}
        />
        <Select
          value={filters.productArea ?? ''}
          onChange={(v) => setFilters((f) => ({ ...f, productArea: v || undefined }))}
          options={[['', 'Alle produktområder'], ...areas.map((a) => [a, a] as [string, string])]}
        />
        <Toggle
          active={Boolean(filters.includeBelowThreshold)}
          onClick={() => setFilters((f) => ({ ...f, includeBelowThreshold: !f.includeBelowThreshold }))}
        >
          Vis under terskel
        </Toggle>
        <Toggle
          active={Boolean(filters.includeClosed)}
          onClick={() => setFilters((f) => ({ ...f, includeClosed: !f.includeClosed }))}
        >
          Vis avsluttede
        </Toggle>
      </div>

      {lastRun && (
        <p className="text-ink-soft text-xs">
          Siste kjøring {formatDate(lastRun.startedAt)} — {lastRun.note ?? ''}
        </p>
      )}

      {isLoading ? (
        <SkeletonPanel lines={6} />
      ) : !data || data.items.length === 0 ? (
        <GlassPanel>
          <EmptyState
            title="Ingen anbud å vise."
            description="Anbudsspeideren kjører hverdager 05:30 og legger nye treff her."
          />
        </GlassPanel>
      ) : (
        <div className="space-y-2">
          {data.items.map((t) => (
            <TenderRow key={t.id} tender={t} onOpen={() => navigate(`/salg/anbud/${t.id}`)} />
          ))}
        </div>
      )}
    </div>
  );
}

function TenderRow({ tender, onOpen }: { tender: TenderDto; onOpen: () => void }) {
  const days = tender.deadline ? daysUntil(tender.deadline) : null;
  const urgent = days !== null && days <= 3 && days >= 0;
  return (
    <button
      onClick={onOpen}
      className="border-hairline bg-panel/60 hover:bg-panel/90 block w-full rounded-lg border p-3 text-left transition-colors"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <ScoreDot score={tender.topScore} />
            <span className="text-ink truncate text-sm font-medium">{tender.title}</span>
            {tender.isSeed && <span className="text-ink-soft text-[10px]">demo</span>}
          </div>
          <p className="text-ink-soft mt-0.5 text-xs">
            {tender.buyerName} · {TENDER_NOTICE_TYPE_LABELS[tender.noticeType]}
            {tender.isBelowThreshold ? ' · under terskel' : ''}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <TenderStatusBadge status={tender.status} size="sm" />
          {tender.deadline && (
            <span
              className={cn(
                'inline-flex items-center gap-1 text-[11px]',
                urgent ? 'text-warning' : 'text-ink-soft',
              )}
            >
              <CalendarClock size={11} aria-hidden />
              {days !== null && days >= 0 ? `frist om ${days} d` : 'frist passert'}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function ScoreDot({ score }: { score: number }) {
  const tone =
    score >= 70 ? 'bg-success' : score >= 45 ? 'bg-accent' : score > 0 ? 'bg-ink-soft' : 'bg-ink-soft/40';
  return (
    <span className="inline-flex items-center gap-1" title={`Relevans ${score}/100`}>
      <span className={cn('h-2 w-2 rounded-full', tone)} aria-hidden />
      <span className="text-ink-soft text-[11px] tabular-nums">{score}</span>
    </span>
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
}) {
  return (
    <select
      className="border-hairline bg-base/40 text-ink rounded-lg border px-3 py-1.5 text-sm"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map(([v, label]) => (
        <option key={v} value={v}>
          {label}
        </option>
      ))}
    </select>
  );
}

function Toggle({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded-lg border px-3 py-1.5 text-xs transition-colors',
        active ? 'bg-accent/15 text-accent border-accent/30' : 'border-hairline text-ink-soft hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}
